import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import {
	ITEM_TYPE,
	SCOPE_TYPE,
	SUBSCOPE_TYPE,
} from 'app/interfaces/actiontracker.interface';
import { IContact, IContactFilters } from 'app/interfaces/contact.interface';
import { AppointmentSidebarFrom } from 'app/interfaces/sidebar.interface';
import { ActionTrackerService } from 'app/services/actiontracker.service';
import { UserService } from 'app/services/user.service';
import { Subject } from 'rxjs';
import {
	debounceTime,
	distinctUntilChanged,
	switchMap,
	tap,
} from 'rxjs/operators';

import { ContactsService } from './../../../../app/services/contacts.service';
import { AppointmentSidebarService } from './../../../appointments/services/appointment-sidebar.service';

export interface IListContact extends IContact {
	isSelected: boolean;
}

@Component({
	selector: 'contacts-list-page',
	templateUrl: './contacts-list-page.component.html',
	styleUrls: ['./contacts-list-page.component.scss'],
})
export class ContactsListPageComponent implements OnInit {
	public search = '';
	public orderBy = 'displayName asc';
	public orderOptions = [
		{ label: 'Sessões mais recentes', value: 'lastEventDate desc' },
		{ label: 'Sessões mais antigas', value: 'lastEventDate asc' },
		{ label: 'Ordem alfabética A-Z', value: 'displayName asc' },
		{ label: 'Ordem alfabética Z-A', value: 'displayName desc' },
	];
	public totalContacts = 0;
	public totalPages = 0;
	public currentPage = 1;
	public contacts: IListContact[] = [];
	public isLoading = false;
	public getContactsSubscription = null;
	public searchSubject: Subject<{
		filters?: IContactFilters;
		search: string;
		orderBy: string;
		page: number;
	}> = new Subject();
	public selectedContacts = [];
	public isAllContactsSelected = false;
	public contactFilterIsVisible = false;
	public contactFilters?: IContactFilters;
	public professionalType = '';
	public selectedFiltersQuantity = 0;
	constructor(
		public router: Router,
		public contactsService: ContactsService,
		public userService: UserService,
		public atService: ActionTrackerService,
		public appointmentSidebarService: AppointmentSidebarService
	) {}

	ngOnInit(): void {
		this.userService.getProfileData().then((data) => {
			this.professionalType = data.mainType;
		});
		this.createGetContactsDebounce();
		this.atService.eventTracker(
			SCOPE_TYPE.Contact,
			SUBSCOPE_TYPE.List,
			ITEM_TYPE.Page,
			null,
			{}
		);
		this.searchSubject.next({
			filters: this.contactFilters,
			search: this.search,
			orderBy: this.orderBy,
			page: this.currentPage,
		});
	}

	addContact() {
		this.atService.eventTracker(
			SCOPE_TYPE.Contact,
			SUBSCOPE_TYPE.List,
			ITEM_TYPE.BtnAdd,
			null,
			{}
		);
		this.router.navigateByUrl('/clients/add');
	}

	createGetContactsDebounce() {
		this.getContactsSubscription = this.searchSubject
			.pipe(
				tap(() => {
					this.isLoading = true;
				}),
				debounceTime(1200),
				distinctUntilChanged((previous, current) => {
					if (previous.search !== current.search) {
						this.atService.eventTracker(
							SCOPE_TYPE.Contact,
							SUBSCOPE_TYPE.List,
							ITEM_TYPE.Search,
							null,
							{}
						);
					}
					return (
						previous.search === current.search &&
						previous.orderBy === current.orderBy &&
						previous.filters === current.filters &&
						previous.page === current.page
					);
				}),
				switchMap(() => {
					return this.contactsService.getContacts(
						this.search,
						30,
						this.currentPage * 30 - 30,
						this.orderBy,
						this.contactFilters,
						this.professionalType
					);
				})
			)
			.subscribe(
				(res) => {
					if (res) {
						this.totalContacts = res.total;
						this.totalPages =
							Math.round(this.totalContacts / 30) > 0
								? Math.round(this.totalContacts / 30)
								: 1;
						this.contacts = res.contacts.map((contact) => {
							return {
								...contact,
								isSelected: !!this.selectedContacts.find((selectedContact) => {
									return selectedContact === contact.refUserId;
								}),
							};
						});
						this.setIfAllContactsSelected();

						this.isLoading = false;
						this.scrollToTop();
						this.trackContactsResult();
					}
				},
				() => {
					this.contacts = [];
					this.isLoading = false;
				}
			);
	}

	onSearchChange(filters?: IContactFilters) {
		this.currentPage = 1;
		if (filters) {
			this.contactFilters = filters;
		}
		this.searchSubject.next({
			filters: this.contactFilters,
			search: this.search,
			orderBy: this.orderBy,
			page: this.currentPage,
		});
	}

	onPageChange(event) {
		if (!event) {
			return;
		}
		this.currentPage = event.page + 1;
		this.searchSubject.next({
			filters: this.contactFilters,
			search: this.search,
			orderBy: this.orderBy,
			page: this.currentPage,
		});
	}

	openAppoitmentSidebar(id) {
		this.appointmentSidebarService.openSidebar(
			{ contactId: id, from: AppointmentSidebarFrom.CLIENTS },
			true
		);
	}

	onOrderByChange() {
		const map = {
			'lastEventDate desc': 'OrderBySessionsDesc',
			'lastEventDate asc': 'OrderBySessionsAsc',
			'displayName asc': 'OrderByAlfAsc',
			'displayName desc': 'OrderByAlfDesc',
		};

		this.currentPage = 1;
		this.searchSubject.next({
			filters: this.contactFilters,
			search: this.search,
			orderBy: this.orderBy,
			page: this.currentPage,
		});
		this.atService.eventTracker(
			SCOPE_TYPE.Contact,
			SUBSCOPE_TYPE.List,
			ITEM_TYPE[map[this.orderBy]],
			null,
			{}
		);
	}

	scrollToTop() {
		const listDom = document.getElementById('contacts-table');
		if (listDom) {
			setTimeout(() => {
				listDom.scrollIntoView({ block: 'start', behavior: 'smooth' });
			}, 500);
		}
	}

	trackContactsResult() {
		if (this.contacts.length > 0) {
			this.atService.eventTracker(
				SCOPE_TYPE.Contact,
				SUBSCOPE_TYPE.List,
				ITEM_TYPE.HasResults,
				null,
				{}
			);
			return;
		}
		this.atService.eventTracker(
			SCOPE_TYPE.Contact,
			SUBSCOPE_TYPE.List,
			ITEM_TYPE.NoResults,
			null,
			{}
		);
	}
	toggleSelectedContacts(event) {
		this.contacts.forEach((contact) => {
			if (contact.refUserId) {
				contact.isSelected = event.checked;
			}
		});
		if (event.checked) {
			this.selectedContacts = [
				...this.selectedContacts,
				...this.contacts
					.filter(
						(contact) =>
							contact.refUserId &&
							!this.selectedContacts.find(
								(selectedContact) => selectedContact === contact.refUserId
							)
					)
					.map((contact) => contact.refUserId),
			];
			return;
		}
		if (!event.checked) {
			this.selectedContacts = this.selectedContacts.filter(
				(selectedContact) => {
					return this.contacts.find((contact) => {
						selectedContact !== contact.refUserId;
					});
				}
			);
		}
	}

	onToggleSingleSelectedContact(event) {
		if (!event.checked) {
			this.selectedContacts = this.selectedContacts.filter((contact) => {
				return contact !== event.refUserId;
			});
			this.setIfAllContactsSelected();
			return;
		}
		this.selectedContacts.push(event.refUserId);
	}

	cleanSelectedContacts() {
		this.selectedContacts = [];
		this.contacts.forEach((contact) => {
			contact.isSelected = false;
		});
		this.isAllContactsSelected = false;
	}

	setIfAllContactsSelected() {
		this.isAllContactsSelected = this.contacts
			.filter((contact) => {
				return contact.refUserId;
			})
			.every((contact) => {
				return !!this.selectedContacts.find((selectedContact) => {
					return selectedContact === contact.refUserId;
				});
			});
	}

	public openFilterSidebar(): void {
		this.contactFilterIsVisible = true;
	}

	public onCloseFilterSidebar(): void {
		this.contactFilterIsVisible = false;
	}
}
